import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Switch, Route, withRouter } from 'react-router-dom'

import CityPlanView from '../../Containers/Campaigns/CityPlanView'
import RosterView from '../../Containers/Campaigns/RosterView'
import CityPlanEdit from '../../Containers/Campaigns/CityPlanEdit'
import { AdminRoute, PrivateRoute } from '../../Config/Routing/auth'
import { PATHS } from '../../Config/Routing/paths'
import LetterboxMissions from '../../Containers/LetterboxMissions/container/LetterboxMissions'
import LetterboxMissionsV2 from '../../Containers/LetterboxMissions/v2/LetterboxMissionsV2'
import { normalizeGraphQLConnection } from '../../shared/graphql/utils'
import { mockRosters } from '../../Utils/Mission'
import {
  callAuthorizedGraphQLEndpoint,
  fetchCityPlanMissionFormChunks,
} from '../../Module/graphQL'
import LoadingIndicator from '../../Components/LoadingIndicator/LoadingIndicator'
import AIRoster from '../../Containers/Campaigns/AIRoster'
import { CityPlanProvider } from '../../Containers/Campaigns/CityPlan/CityPlanContext'
import { useCityPlanStats } from '../../Containers/Campaigns/CityPlan/hooks/useCityPlanStats'
import { isCityPlanV2 } from '../../Containers/Campaigns/CityPlan/flags'
import {
  isoWeekKey,
  parseIsoWeekKey,
  rostersFromStats,
} from '../../Utils/CityPlan'

/**
 * The city plan route family.
 *
 * Letterbox plans (3,000+ missions) never load their missions here: the
 * router loads the campaign and city plan metadata once, and each page asks
 * the API for exactly the slice it shows (see Containers/Campaigns/CityPlan).
 * Refreshing counts or missions bumps a version in the context; nothing
 * unmounts, so selections, filters and scroll positions survive an edit.
 *
 * Other campaign types keep the legacy behaviour: every mission loaded up
 * front and rosters derived from them.
 */
const CityPlanRouter = props => {
  const [isLoaded, setIsLoaded] = useState(false)
  const [campaign, setCampaign] = useState({})
  const [missionsVersion, setMissionsVersion] = useState(0)

  const { campaignId, cityPlanId, rosterId } = props.match.params
  const v2 = isCityPlanV2(campaign.campaign, props.location)

  const stats = useCityPlanStats(v2 ? cityPlanId : null)

  useEffect(() => {
    fetchData({ campaignId, cityPlanId })
  }, [])

  /**
   * Loads the campaign and city plan; for letterbox plans the missions are
   * skipped (`withMissions` false) and the pages fetch their own.
   */
  const fetchData = async ({ campaignId, cityPlanId }) => {
    setIsLoaded(false)
    try {
      const result = await getCityPlanData({
        campaignId,
        cityPlanId,
        missionChunk: 200,
        location: props.location,
      })

      const formattedData = formatData(result)
      setCampaign(formattedData)
    } catch (e) {
      setCampaign(null)
      throw e
    } finally {
      setIsLoaded(true)
    }
  }

  const refreshMissions = useCallback(() => {
    setMissionsVersion(version => version + 1)
  }, [])

  const onGenerateMissionsCallback = () => {
    if (v2) {
      stats.refetch()
      refreshMissions()
      return
    }
    fetchData({ campaignId, cityPlanId })
  }

  const contextValue = useMemo(
    () => ({
      campaign: campaign.campaign,
      cityPlan: campaign.cityPlan,
      city: campaign.city,
      countryCode: campaign.countryCode,
      isLetterbox: v2,
      stats: stats.stats,
      statsLoading: stats.loading,
      statsError: stats.error,
      refreshStats: stats.refetch,
      missionsVersion,
      refreshMissions,
    }),
    [
      campaign,
      v2,
      stats.stats,
      stats.loading,
      stats.error,
      stats.refetch,
      missionsVersion,
      refreshMissions,
    ],
  )

  if (!isLoaded) return <LoadingIndicator />

  // On v2 the rosters come from the counts, not from the missions.
  const rosters = v2 ? rostersFromStats(stats.stats) : campaign.rosters
  const pageProps = { ...props, ...campaign, rosters }

  return (
    <CityPlanProvider value={contextValue}>
      <Switch>
        <Route exact path={props.match.path}>
          <PrivateRoute>
            <CityPlanView {...pageProps} />
          </PrivateRoute>
        </Route>
        <Route
          exact
          path={`${props.match.path}${PATHS.cityPlan.letterboxMissions}`}
        >
          <PrivateRoute>
            {v2 ? (
              <LetterboxMissionsV2 {...pageProps} />
            ) : (
              <LetterboxMissions
                refetchMissions={(args = {}) =>
                  fetchData({ campaignId, cityPlanId, ...args })
                }
                {...pageProps}
              />
            )}
          </PrivateRoute>
        </Route>
        <Route exact path={`${props.match.path}${PATHS.cityPlan.rosterView}`}>
          <PrivateRoute>
            {v2 ? (
              <LetterboxMissionsV2 {...pageProps} weekScoped />
            ) : (
              <RosterView
                {...pageProps}
                missions={
                  rosterId && getRostersMissions(rosterId, campaign.rosters)
                }
                refetchMissions={(args = {}) =>
                  fetchData({ campaignId, cityPlanId, ...args })
                }
              />
            )}
          </PrivateRoute>
        </Route>
        <Route exact path={`${props.match.path}${PATHS.cityPlan.cityPlanEdit}`}>
          <AdminRoute>
            <CityPlanEdit {...pageProps} />
          </AdminRoute>
        </Route>
        <Route exact path={`${props.match.path}${PATHS.cityPlan.aiRoster}`}>
          <AdminRoute>
            <AIRoster
              {...pageProps}
              onGenerateMissionsCallback={onGenerateMissionsCallback}
            />
          </AdminRoute>
        </Route>
      </Switch>
    </CityPlanProvider>
  )
}

const getCityPlanData = async ({
  campaignId,
  cityPlanId,
  missionChunk,
  location,
}) => {
  const gqlCampaign = `query 
    (
    $campaignId:ID!
    ) {
      campaign(id:$campaignId){
        id
        name
        type
        status
        startDate
        bonusEnabled
        client {
          name
          industry
          id
        }
        metrics {
          nodes {
            id
            name
            targetConversionRate
            targetCostPerMetric
            isPrimary 
          }
        }
        isSelfServe
        country {
            code
            countryDefaultSettings {
              h2hDistributionCosts,
              d2dDistributionCosts,

            },
            citiesSettings {
              nodes {
                id,
                name, 
                h2hDistributionCosts,
                d2dDistributionCosts,
              }
            },
        }
      }
    }`

  const gqlCityPlan = `
  query ($cityPlanId:ID!) {
    cityPlans(cityPlanId: $cityPlanId) {
      edges {
          node {
            id
            hourlyPayout
            clientMarginPercent
            distributionCost
            payout
            hardcodedBasePayout
            city {
              id
              name
              latitude
              longitude
              morningCheckInTime
              lunchCheckInTime
              afternoonCheckInTime
              teamLeaderEnabled
              timezone
              location {
                longitude
                latitude
              }
              locations{
                edges {
                  node {
                    id
                    name
                    longitude
                    latitude
                    isSelfServe
                    campaignId
                    status
                  }
                }
              }
              country {
                code
                currencySign 
              }
            }
          }
      }
    }
  }
`
  const gqlMissions = `
   query ($cityPlanId:ID!, $first: Int, $skip: Int, $includeInventory: Boolean) {
    cityPlans(cityPlanId: $cityPlanId) {
      edges {
        node {
           id
            missions(first: $first, skip: $skip, includeInventory: $includeInventory) {
              pageInfo {
                hasNextPage
              }
              edges {
                node {
                  id
                  flyerCode
                  status
                  isReadyToPublish
                  totalFlyersDistributed
                  dateTime
                  distributionCost
                  draftPayAdjustment
                  printingCostPerFlyer
                  bonusPerConversion
                  bonusThreshold
                  bonusRedemptionPeriod
                  cancelled
                  automatic
                  cvrPredictionPercent
                  type
                  boundaryColour
                  label
                  packSize
                  meetingPoint
                  boundary {
                    latitude
                    longitude
                  }
                  user {
                    id
                    firstName
                    lastName
                  }
                  location {
                    id
                    name
                    detailedRank
                    longitude
                    latitude
                  }
                  costPerFlyer
                  amountScans
                  canBeSpied
                  spyInformation{
                    spy{
                      id
                      firstName
                      lastName
                    }
                    spyDetails{
                      checkInTime
                      checkoutTime
                      reportReason
                      }
                    shift{
                      id
                    }
                  }
                  teamLeaderGroupId
                  teamLeaderId
                  timeOfDay
                  meetingPoint
                }
              }
            }
        }
      }
    }
  }
  `

  const campaignResponse = await callAuthorizedGraphQLEndpoint(gqlCampaign, {
    campaignId,
  })
  const campaign = await campaignResponse.json()
  const campaignResult = campaign.data.campaign

  const getCityPlan = async () => {
    const cityPlanResponse = await callAuthorizedGraphQLEndpoint(gqlCityPlan, {
      cityPlanId,
    })
    const resCityPlanResponse = await cityPlanResponse.json()
    const cityPlan = resCityPlanResponse.data.cityPlans.edges[0].node

    // Letterbox plans page their missions on each screen instead; loading
    // 3,000 polygons here is what made these pages unusable.
    if (isCityPlanV2(campaignResult, location)) {
      return { ...cityPlan, missions: { edges: [] } }
    }

    const { missions } = await fetchCityPlanMissionFormChunks({
      cityPlanId,
      chunkSize: missionChunk,
      skip: 0,
      gql: gqlMissions,
    })

    return {
      ...cityPlan,
      missions: {
        edges: missions,
      },
    }
  }

  const cityPlan = await getCityPlan()

  return { ...campaignResult, cityPlans: { nodes: cityPlan } }
}

const formatData = campaignData => {
  if (!campaignData) {
    return {
      campaign: {},
      cityPlan: {},
      rosters: [],
      metrics: [],
    }
  }
  const cityPlan = campaignData.cityPlans.nodes

  const cityPlanMissions = normalizeGraphQLConnection(cityPlan.missions)
  const rosters = mockRosters(cityPlanMissions)

  return {
    campaign: campaignData,
    cityPlan: {
      ...cityPlan,
      missions: cityPlanMissions,
    },
    city: cityPlan.city,
    rosters,
    countryCode: cityPlan.city.country.code,
  }
}

const getRostersMissions = (rosterId, rosters) => {
  const [year, week] = rosterId.split('-')
  const roster = rosters.find(
    roster => roster.weekNum == week && roster.year == year,
  )
  return roster && roster.missions ? roster.missions : []
}

export default withRouter(CityPlanRouter)
